// src/pages/Convocations/components/ConvocationsTable.jsx

import React from "react";

import {
  FaCheck,
  FaCommentDots,
  FaMapMarkerAlt,
  FaUserTie,
} from "react-icons/fa";

import ConvocationStatusBadge from "../ui/ConvocationStatusBadge";
import ConvocationActions from "./ConvocationActions";
import ConvocationSupportBadges from "./ConvocationSupportBadges";

import {
  formatDate,
  formatDateTime,
  getConvocationActorLabel,
  getEventLocation,
} from "../utils/convocation.utils";

const TABLE_HEADERS = [
  "Date",
  "Événement",
  "Affectation",
  "Réponse",
  "Informations",
  "Actions",
];

export default function ConvocationsTable({
  convocations,
  saving,
  onOpenEvent,
  onOpenText,
  onRespond,
}) {
  return (
    <div
      className="
        hidden overflow-hidden
        rounded-3xl
        border border-slate-200/80
        bg-white
        shadow-[0_24px_70px_-42px_rgba(15,23,42,0.55)]
        dark:border-slate-800
        dark:bg-slate-900
        xl:block
      "
    >
      <div className="overflow-x-auto">
        <table
          className="
            min-w-full
            border-collapse
            text-left
            text-sm
          "
        >
          <thead
            className="
              bg-slate-900
              text-white
              dark:bg-slate-950
            "
          >
            <tr>
              {TABLE_HEADERS.map((label) => (
                <th
                  key={label}
                  className="
                    border-b
                    border-white/10
                    px-5 py-4
                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-[0.14em]
                    text-slate-300
                  "
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            className="
              divide-y
              divide-slate-100
              dark:divide-slate-800
            "
          >
            {convocations.map((convocation) => {
              const event = convocation?.Event || {};

              const hasObservations = Boolean(
                convocation.observations,
              );

              const hasRejectReason = Boolean(
                convocation.response_reason,
              );

              const hasSupportComment = Boolean(
                convocation.support_request_comment,
              );

              return (
                <tr
                  key={convocation.id}
                  className="
                    align-top
                    transition-colors
                    hover:bg-slate-50/80
                    dark:hover:bg-slate-800/45
                  "
                >
                  {/* Date */}
                  <td className="whitespace-nowrap px-5 py-5">
                    <div
                      className="
                        inline-flex
                        min-w-[105px]
                        items-center
                        justify-center
                        rounded-2xl
                        bg-blue-50
                        px-3 py-3
                        text-blue-800
                        dark:bg-blue-950/40
                        dark:text-blue-200
                      "
                    >
                      <span className="text-sm font-semibold">
                        {formatDate(event.start)}
                      </span>
                    </div>
                  </td>

                  {/* Événement */}
                  <td className="max-w-[310px] px-5 py-5">
                    <h3
                      className="
                        font-satoshi
                        text-base font-bold
                        leading-snug
                        text-slate-950
                        dark:text-white
                      "
                    >
                      {event.titre || "Événement"}
                    </h3>

                    <p
                      className="
                        mt-2 flex
                        items-start gap-2
                        text-xs
                        font-medium
                        leading-5
                        text-slate-500
                        dark:text-slate-400
                      "
                    >
                      <FaMapMarkerAlt
                        className="
                          mt-0.5
                          shrink-0
                          text-rose-500
                        "
                      />

                      {getEventLocation(event)}
                    </p>

                    {event.type_event ? (
                      <span
                        className="
                          mt-2 inline-flex
                          rounded-full
                          bg-slate-100
                          px-2.5 py-1
                          text-[11px]
                          font-semibold
                          text-slate-600
                          dark:bg-slate-800
                          dark:text-slate-300
                        "
                      >
                        {event.type_event}
                      </span>
                    ) : null}
                  </td>

                  {/* Affectation */}
                  <td className="px-5 py-5">
                    <div
                      className="
                        inline-flex
                        items-center gap-2
                        font-semibold
                        text-slate-800
                        dark:text-white
                      "
                    >
                      <FaUserTie className="text-blue-600 dark:text-blue-400" />

                      {convocation.poste || "Non précisée"}
                    </div>

                    {convocation.tapis ? (
                      <p
                        className="
                          mt-1 text-xs
                          font-medium
                          text-slate-500
                          dark:text-slate-400
                        "
                      >
                        Tapis :{" "}
                        <span className="font-semibold">
                          {convocation.tapis}
                        </span>
                      </p>
                    ) : null}

                    <div
                      className="
                        mt-3
                        border-t
                        border-slate-100
                        pt-3
                        dark:border-slate-800
                      "
                    >
                      <p
                        className="
                          text-[10px]
                          font-semibold
                          uppercase
                          tracking-[0.12em]
                          text-slate-400
                        "
                      >
                        Profil acteur
                      </p>

                      <p
                        className="
                          mt-1 text-xs
                          font-semibold
                          text-slate-700
                          dark:text-slate-200
                        "
                      >
                        {getConvocationActorLabel(
                          convocation,
                        )}
                      </p>

                      {convocation?.acteur?.club_acteur ? (
                        <p
                          className="
                            mt-1 max-w-[180px]
                            truncate
                            text-[11px]
                            font-medium
                            text-slate-500
                            dark:text-slate-400
                          "
                        >
                          {convocation.acteur.club_acteur}
                        </p>
                      ) : null}
                    </div>
                  </td>

                  {/* Réponse */}
                  <td className="min-w-[210px] px-5 py-5">
                    <ConvocationStatusBadge
                      status={convocation.response_status}
                    />

                    {convocation.responded_at ? (
                      <p
                        className="
                          mt-2 text-[11px]
                          font-medium
                          text-slate-500
                          dark:text-slate-400
                        "
                      >
                        Répondu le{" "}
                        {formatDateTime(
                          convocation.responded_at,
                        )}
                      </p>
                    ) : null}

                    <div className="mt-3">
                      <ConvocationSupportBadges
                        convocation={convocation}
                      />
                    </div>
                  </td>

                  {/* Informations */}
                  <td className="min-w-[205px] px-5 py-5">
                    <div className="space-y-2">
                      <div
                        className="
                          flex items-center
                          justify-between
                          gap-3
                          text-xs
                          font-medium
                          text-slate-500
                          dark:text-slate-400
                        "
                      >
                        <span>Validation admin</span>

                        <span
                          className={
                            convocation.is_validate
                              ? [
                                  "inline-flex",
                                  "items-center",
                                  "gap-1",
                                  "font-semibold",
                                  "text-emerald-600",
                                  "dark:text-emerald-400",
                                ].join(" ")
                              : "font-semibold text-slate-500"
                          }
                        >
                          {convocation.is_validate ? (
                            <FaCheck />
                          ) : null}

                          {convocation.is_validate
                            ? "Oui"
                            : "Non"}
                        </span>
                      </div>

                      {convocation.note != null ? (
                        <div
                          className="
                            flex items-center
                            justify-between
                            gap-3
                            text-xs
                            font-medium
                            text-slate-500
                            dark:text-slate-400
                          "
                        >
                          <span>Note</span>

                          <span
                            className="
                              font-semibold
                              text-slate-700
                              dark:text-slate-200
                            "
                          >
                            {convocation.note}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div
                      className="
                        mt-3 flex
                        flex-col
                        items-start
                        gap-2
                      "
                    >
                      {hasObservations ? (
                        <button
                          type="button"
                          onClick={() =>
                            onOpenText({
                              title:
                                "Observations des responsables",
                              content:
                                convocation.observations,
                              allowHtml: true,
                            })
                          }
                          className="
                            inline-flex
                            items-center
                            gap-1.5
                            text-xs
                            font-semibold
                            text-blue-700
                            underline
                            decoration-blue-300
                            underline-offset-4
                            hover:text-blue-900
                            dark:text-blue-300
                            dark:hover:text-blue-200
                          "
                        >
                          <FaCommentDots />
                          Observations
                        </button>
                      ) : null}

                      {hasRejectReason ? (
                        <button
                          type="button"
                          onClick={() =>
                            onOpenText({
                              title: "Motif de votre refus",
                              content:
                                convocation.response_reason,
                              allowHtml: false,
                            })
                          }
                          className="
                            inline-flex
                            items-center
                            gap-1.5
                            text-xs
                            font-semibold
                            text-rose-700
                            underline
                            decoration-rose-300
                            underline-offset-4
                            hover:text-rose-900
                            dark:text-rose-300
                            dark:hover:text-rose-200
                          "
                        >
                          <FaCommentDots />
                          Motif du refus
                        </button>
                      ) : null}

                      {hasSupportComment ? (
                        <button
                          type="button"
                          onClick={() =>
                            onOpenText({
                              title:
                                "Commentaire de prise en charge",
                              content:
                                convocation.support_request_comment,
                              allowHtml: false,
                            })
                          }
                          className="
                            inline-flex
                            items-center
                            gap-1.5
                            text-xs
                            font-semibold
                            text-violet-700
                            underline
                            decoration-violet-300
                            underline-offset-4
                            hover:text-violet-900
                            dark:text-violet-300
                            dark:hover:text-violet-200
                          "
                        >
                          <FaCommentDots />
                          Prise en charge
                        </button>
                      ) : null}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="min-w-[175px] px-5 py-5">
                    <ConvocationActions
                      convocation={convocation}
                      saving={saving}
                      stacked
                      onOpenEvent={onOpenEvent}
                      onRespond={onRespond}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}